import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams, Link, useLocation } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { validateResetToken, resetPassword, clearError } from '../../store/slices/authSlice';
import toast from 'react-hot-toast';

const ResetPasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { loading, error } = useSelector((state) => state.auth);

  // Determine if this is an Airliner page based on URL
  const isAirliner = location.pathname.includes('/airliner');
  const forgotPasswordPath = isAirliner ? '/airliner/auth/forgot-password' : '/forgot-password';
  const loginPath = isAirliner ? '/airliner/auth/login' : '/';

  const [tokenValid, setTokenValid] = useState(false);
  const [validating, setValidating] = useState(true);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const params = useParams();
  const tokenFromParams = params.token;
  const tokenFromQuery = searchParams.get('token');
  const token = tokenFromParams || tokenFromQuery;
  const email = searchParams.get('email');

  useEffect(() => {
    dispatch(clearError());

    const validateToken = async () => {
      if (!token || !email) {
        setValidating(false);
        return;
      }

      try {
        await dispatch(validateResetToken({ token, email })).unwrap();
        setTokenValid(true);
        toast.success('Reset link verified');
      } catch (err) {
        setTokenValid(false);
        toast.error('Invalid or expired reset link');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, email, dispatch]);

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = 'Please provide a password';
    } else if (formData.password.length < 11) {
      errors.password = 'Password must be at least 11 characters long';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      errors.password = 'Password must include lowercase letters';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      errors.password = 'Password must include uppercase letters';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must include numbers';
    } else if (!/\W/.test(formData.password)) {
      errors.password = 'Password must include symbols';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please provide a confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'New password does not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      await dispatch(resetPassword({
        token,
        email,
        password: formData.password,
        password_confirmation: formData.confirmPassword
      })).unwrap();

      setSuccess(true);
      toast.success('Password reset successful!');
      setTimeout(() => navigate(loginPath), 3000);
    } catch (err) {
      console.error('Failed to reset password:', err);
      toast.error(err || 'Failed to reset password');
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-jet-background pt-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-jet-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (!token || !email || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-jet-background px-4 pt-20">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-semibold text-jet-primary mb-3">Invalid Reset Link</h2>
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link
            to={forgotPasswordPath}
            className="inline-block px-6 py-3 bg-jet-yellow hover:bg-jet-yellow/90 text-jet-primary rounded-lg font-semibold transition-all"
          >
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-jet-background px-4 pt-20">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-semibold text-jet-primary mb-3">Password Reset Successful!</h2>
          <p className="text-gray-600 mb-4">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <p className="text-sm text-gray-500 mb-6">Redirecting to home page...</p>
          <Link
            to={loginPath}
            className="inline-block px-6 py-3 bg-jet-yellow hover:bg-jet-yellow/90 text-jet-primary rounded-lg font-semibold transition-all"
          >
            Go to {isAirliner ? 'Login' : 'Home'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-jet-background px-4 pt-20">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-jet-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-jet-orange" />
          </div>
          <h2 className="text-3xl font-semibold text-jet-primary mb-2">Reset Password</h2>
          <p className="text-gray-600">Enter your new password below</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-jet-primary mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-jet-yellow focus:border-transparent transition-all ${validationErrors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {validationErrors.password && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Must be at least 11 characters with uppercase, lowercase, and number
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-jet-primary mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-jet-yellow focus:border-transparent transition-all ${validationErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {validationErrors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-jet-yellow hover:bg-jet-yellow/90 text-jet-primary rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-jet-primary/30 border-t-jet-primary rounded-full animate-spin"></div>
                Resetting...
              </span>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to={loginPath}
            className="text-sm text-gray-600 hover:text-jet-orange transition-colors"
          >
            Back to {isAirliner ? 'Login' : 'Home'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
